import { useState, useEffect, useMemo } from 'react';
import { cn, timeAgo, formatDate } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import type { Script } from '@/types';

interface AutomationRule {
  id: string;
  name: string;
  enabled: boolean;
  trigger: {
    type: string;
    description: string;
    threshold?: number;
  };
  action: {
    type: 'run_script' | 'reboot' | 'alert' | 'install_patches';
    description: string;
    scriptId?: string;
  };
  target: {
    type: 'all' | 'client' | 'site' | 'tag';
    value: string;
  };
  schedule: string;
  lastRun: string | null;
  nextRun: string | null;
  runCount: number;
  successCount: number;
  failCount: number;
}

const TRIGGER_TYPES = [
  { value: 'device_offline', label: 'Device Goes Offline' },
  { value: 'high_cpu', label: 'High CPU Usage' },
  { value: 'high_memory', label: 'High Memory Usage' },
  { value: 'low_disk', label: 'Low Disk Space' },
  { value: 'service_stopped', label: 'Service Stopped' },
  { value: 'process_detected', label: 'Process Detected' },
  { value: 'process_missing', label: 'Process Missing' },
  { value: 'event_log_match', label: 'Event Log Match' },
  { value: 'failed_update', label: 'Failed Update' },
  { value: 'pending_reboot', label: 'Pending Reboot' },
  { value: 'antivirus_inactive', label: 'Antivirus Inactive' },
  { value: 'agent_outdated', label: 'Agent Outdated' },
  { value: 'network_change', label: 'Network Change' },
  { value: 'hardware_change', label: 'Hardware Change' },
];

const ACTION_TYPES = [
  { value: 'run_script', label: 'Run Script' },
  { value: 'reboot', label: 'Reboot Device' },
  { value: 'alert', label: 'Create Alert' },
  { value: 'install_patches', label: 'Install Patches' },
];

const initialRules: AutomationRule[] = [
  {
    id: 'auto-1', name: 'Auto-restart service on crash', enabled: true,
    trigger: { type: 'service_stopped', description: 'When SQL Server service stops' },
    action: { type: 'run_script', description: 'Restart SQL Service', scriptId: 'script-1' },
    target: { type: 'tag', value: 'database' },
    schedule: 'Real-time', lastRun: new Date(Date.now() - 3600000).toISOString(),
    nextRun: null, runCount: 47, successCount: 45, failCount: 2,
  },
  {
    id: 'auto-2', name: 'Alert on high CPU', enabled: true,
    trigger: { type: 'high_cpu', description: 'CPU > 90% for 5 minutes', threshold: 90 },
    action: { type: 'alert', description: 'Create high CPU alert' },
    target: { type: 'all', value: 'All devices' },
    schedule: 'Every 5 min', lastRun: new Date(Date.now() - 300000).toISOString(),
    nextRun: new Date(Date.now() + 300000).toISOString(), runCount: 312, successCount: 312, failCount: 0,
  },
  {
    id: 'auto-3', name: 'Auto-install critical patches', enabled: true,
    trigger: { type: 'failed_update', description: 'When a patch installation fails' },
    action: { type: 'install_patches', description: 'Reinstall failed patches' },
    target: { type: 'tag', value: 'ring1' },
    schedule: 'Maintenance window: Sun 2-4 AM', lastRun: new Date(Date.now() - 86400000 * 2).toISOString(),
    nextRun: new Date(Date.now() + 86400000 * 5).toISOString(), runCount: 18, successCount: 16, failCount: 2,
  },
  {
    id: 'auto-4', name: 'Reboot on pending reboot', enabled: false,
    trigger: { type: 'pending_reboot', description: 'When reboot is pending for > 3 days' },
    action: { type: 'reboot', description: 'Reboot device after hours' },
    target: { type: 'client', value: 'Acme Corp' },
    schedule: 'Daily at 2:00 AM', lastRun: null, nextRun: null, runCount: 5, successCount: 4, failCount: 1,
  },
  {
    id: 'auto-5', name: 'Alert on disk space low', enabled: true,
    trigger: { type: 'low_disk', description: 'Disk free space < 10%', threshold: 10 },
    action: { type: 'alert', description: 'Create low disk alert' },
    target: { type: 'all', value: 'All devices' },
    schedule: 'Every 15 min', lastRun: new Date(Date.now() - 900000).toISOString(),
    nextRun: new Date(Date.now() + 900000).toISOString(), runCount: 89, successCount: 89, failCount: 0,
  },
  {
    id: 'auto-6', name: 'Restart agent on disconnect', enabled: true,
    trigger: { type: 'device_offline', description: 'When device goes offline > 2 min' },
    action: { type: 'alert', description: 'Create device offline alert' },
    target: { type: 'all', value: 'All devices' },
    schedule: 'Real-time', lastRun: new Date(Date.now() - 7200000).toISOString(),
    nextRun: null, runCount: 23, successCount: 23, failCount: 0,
  },
];

export function Automation() {
  const [rules, setRules] = useState<AutomationRule[]>(initialRules);
  const [scripts, setScripts] = useState<Script[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewRule, setShowNewRule] = useState(false);
  const [filter, setFilter] = useState('all');

  // New rule form state
  const [newRule, setNewRule] = useState({
    name: '',
    triggerType: 'high_cpu',
    triggerThreshold: '90',
    actionType: 'alert',
    scriptId: '',
    targetType: 'all',
    targetValue: '',
    schedule: '',
  });

  useEffect(() => {
    mockApi.getScripts().then((s) => {
      setScripts(s);
      setLoading(false);
    });
  }, []);

  const filteredRules = useMemo(() => {
    if (filter === 'all') return rules;
    if (filter === 'enabled') return rules.filter(r => r.enabled);
    if (filter === 'disabled') return rules.filter(r => !r.enabled);
    return rules;
  }, [rules, filter]);

  const toggleRule = (id: string) => {
    setRules(rules.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
  };

  const handleCreateRule = () => {
    if (!newRule.name) return;
    const triggerType = TRIGGER_TYPES.find(t => t.value === newRule.triggerType);
    const actionType = ACTION_TYPES.find(a => a.value === newRule.actionType);
    const script = scripts.find(s => s.id === newRule.scriptId);
    
    const rule: AutomationRule = {
      id: 'auto-' + Date.now(),
      name: newRule.name,
      enabled: true,
      trigger: {
        type: newRule.triggerType,
        description: triggerType?.label || newRule.triggerType,
        threshold: newRule.triggerThreshold ? Number(newRule.triggerThreshold) : undefined,
      },
      action: {
        type: newRule.actionType as any,
        description: actionType?.label || newRule.actionType,
        scriptId: newRule.scriptId || undefined,
      },
      target: {
        type: newRule.targetType as any,
        value: newRule.targetValue || 'All devices',
      },
      schedule: newRule.schedule || 'Real-time',
      lastRun: null,
      nextRun: null,
      runCount: 0,
      successCount: 0,
      failCount: 0,
    };
    setRules([rule, ...rules]);
    setShowNewRule(false);
    setNewRule({ name: '', triggerType: 'high_cpu', triggerThreshold: '90', actionType: 'alert', scriptId: '', targetType: 'all', targetValue: '', schedule: '' });
    const event = new CustomEvent('toast', { detail: { message: `Automation rule "${rule.name}" created`, type: 'success' } });
    window.dispatchEvent(event);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[var(--text-muted)] text-sm">Loading automation rules…</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Automation</h1>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">
            {rules.filter(r => r.enabled).length} active rules · {rules.length} total
          </p>
        </div>
        <Button onClick={() => setShowNewRule(true)}>New Rule</Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1">
        {['all', 'enabled', 'disabled'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'px-3 py-1.5 text-sm rounded-md transition-colors',
              filter === f ? 'bg-[var(--accent)] text-white' : 'text-[var(--text-muted)] hover:bg-[var(--hover)]'
            )}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Rules */}
      <div className="space-y-3">
        {filteredRules.map((rule) => (
          <Card key={rule.id} className="p-4">
            <div className="flex items-start gap-4">
              {/* Toggle */}
              <button
                onClick={() => toggleRule(rule.id)}
                className={cn(
                  'mt-1 w-9 h-5 rounded-full transition-colors flex items-center shrink-0',
                  rule.enabled ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'
                )}
              >
                <span className={cn(
                  'w-4 h-4 bg-white rounded-full transition-transform ml-0.5',
                  rule.enabled && 'translate-x-4'
                )} />
              </button>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-[var(--text-primary)]">{rule.name}</h3>
                  {rule.enabled && <Badge variant="success">Active</Badge>}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
                  <span className="text-[var(--text-muted)]">
                    <span className="text-[var(--text-secondary)]">Trigger:</span> {rule.trigger.description}
                  </span>
                  <span className="text-[var(--text-muted)]">
                    <span className="text-[var(--text-secondary)]">Action:</span> {rule.action.description}
                  </span>
                  <span className="text-[var(--text-muted)]">
                    <span className="text-[var(--text-secondary)]">Target:</span> {rule.target.value}
                  </span>
                  <span className="text-[var(--text-muted)]">
                    <span className="text-[var(--text-secondary)]">Schedule:</span> {rule.schedule}
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6 shrink-0 text-right">
                <div>
                  <p className="text-xs text-[var(--text-muted)]">Runs</p>
                  <p className="text-sm font-medium text-[var(--text-primary)] tabular-nums">{rule.runCount}</p>
                </div>
                <div>
                  <p className="text-xs text-[var(--text-muted)]">Success</p>
                  <p className="text-sm font-medium text-[var(--status-online)] tabular-nums">{rule.successCount}</p>
                </div>
                <div>
                  <p className="text-xs text-[var(--text-muted)]">Failed</p>
                  <p className="text-sm font-medium text-[var(--status-critical)] tabular-nums">{rule.failCount}</p>
                </div>
                <div>
                  <p className="text-xs text-[var(--text-muted)]">Last Run</p>
                  <p className="text-sm text-[var(--text-secondary)]">{rule.lastRun ? timeAgo(rule.lastRun) : 'Never'}</p>
                </div>
              </div>
            </div>
          </Card>
        ))}
        {filteredRules.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-[var(--text-muted)]">No automation rules found.</p>
          </div>
        )}
      </div>

      {/* New Rule Modal */}
      {showNewRule && (
        <Modal open={showNewRule} onClose={() => setShowNewRule(false)} title="New Automation Rule" size="lg">
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[var(--text-secondary)] block mb-1.5">Rule Name</label>
              <input
                type="text"
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                placeholder="e.g., Auto-restart on high memory"
                className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[var(--text-secondary)] block mb-1.5">Trigger</label>
                <select
                  value={newRule.triggerType}
                  onChange={(e) => setNewRule({ ...newRule, triggerType: e.target.value })}
                  className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
                >
                  {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-[var(--text-secondary)] block mb-1.5">Threshold (optional)</label>
                <input
                  type="text"
                  value={newRule.triggerThreshold}
                  onChange={(e) => setNewRule({ ...newRule, triggerThreshold: e.target.value })}
                  placeholder="e.g., 90"
                  className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[var(--text-secondary)] block mb-1.5">Action</label>
                <select
                  value={newRule.actionType}
                  onChange={(e) => setNewRule({ ...newRule, actionType: e.target.value })}
                  className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
                >
                  {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              {newRule.actionType === 'run_script' && (
                <div>
                  <label className="text-sm text-[var(--text-secondary)] block mb-1.5">Script</label>
                  <select
                    value={newRule.scriptId}
                    onChange={(e) => setNewRule({ ...newRule, scriptId: e.target.value })}
                    className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
                  >
                    <option value="">Select script…</option>
                    {scripts.filter(s => s.approvalStatus === 'approved').map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[var(--text-secondary)] block mb-1.5">Target Type</label>
                <select
                  value={newRule.targetType}
                  onChange={(e) => setNewRule({ ...newRule, targetType: e.target.value })}
                  className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
                >
                  <option value="all">All Devices</option>
                  <option value="client">By Client</option>
                  <option value="site">By Site</option>
                  <option value="tag">By Tag</option>
                </select>
              </div>
              {newRule.targetType !== 'all' && (
                <div>
                  <label className="text-sm text-[var(--text-secondary)] block mb-1.5">Target Value</label>
                  <input
                    type="text"
                    value={newRule.targetValue}
                    onChange={(e) => setNewRule({ ...newRule, targetValue: e.target.value })}
                    placeholder="e.g., Acme Corp"
                    className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="text-sm text-[var(--text-secondary)] block mb-1.5">Schedule</label>
              <input
                type="text"
                value={newRule.schedule}
                onChange={(e) => setNewRule({ ...newRule, schedule: e.target.value })}
                placeholder="e.g., Real-time, Every 5 min, Daily at 2:00 AM"
                className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowNewRule(false)}>Cancel</Button>
              <Button onClick={handleCreateRule} disabled={!newRule.name}>Create Rule</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
